import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useReactMediaRecorder } from 'react-media-recorder';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { validationMessages } from '../../config/validationMessages';
import {
  ArrowsAngleContract,
  ArrowsFullscreen,
  ArrowRepeat,
  CameraVideo,
  CameraVideoFill,
  CheckCircleFill,
  ChevronRight,
  CloudUploadFill,
  FileEarmarkFill,
  Mic,
  RecordCircleFill,
  ShieldLock,
  StopFill,
  Wifi,
  X,
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/api';
import UploadSupportingDocument from '../UploadSupportingDocument';
import '../tailwind-shell.css';
import './RecordOrUploadModal.css';
import { patchRecordedWebmDuration } from '../../utils/patchRecordedWebmDuration';
import {
  getRecordingMimeType,
  getRecordingPermissionErrorMessage,
  PROMPT_RECORDING_MEDIA,
} from '../../utils/mediaCapture';

interface PromptSelectionModalProps {
  editingIndex: number | null;
  recordOrUploadModal: boolean;
  onModalClose?: () => void;
  errors: any;
  setErrors: Dispatch<SetStateAction<any>>;
  setVideoFiles: Dispatch<SetStateAction<any>>;
  documentFiles?: { [key: number]: File };
  setDocumentFiles?: Dispatch<SetStateAction<{ [key: number]: File }>>;
  setEditingIndex?: Dispatch<SetStateAction<number | null>>;
  videoFiles: any;
  openPromptIndex: number | null;
  hideSubmit?: boolean;
  promptText?: string;
  /** Profile edit: use full recording studio (step 2) instead of inline recorder on step 1. */
  preferRecordingStudio?: boolean;
  onClearVideoAtIndex?: (index: number) => void;
  uploadProgressSlot?: React.ReactNode;
  /** Called after a video is attached when hideSubmit is true (parent can advance wizard). */
  onVideoAttached?: () => void;
  /** Called with the captured file when hideSubmit is true (preferred for edit flows). */
  onVideoCaptured?: (file: File) => void;
  /** Open directly to the full recording studio (step 2). */
  studioOnly?: boolean;
}

/** Record / upload step uses v2 card layout (no legacy PlayIcon/UploadIcon). */
const RecordOrUploadModal: React.FC<PromptSelectionModalProps> = ({
  editingIndex,
  recordOrUploadModal,
  onModalClose,
  errors,
  setErrors,
  setVideoFiles,
  documentFiles,
  setDocumentFiles,
  setEditingIndex,
  videoFiles,
  openPromptIndex,
  hideSubmit,
  promptText,
  preferRecordingStudio = Boolean(hideSubmit),
  onClearVideoAtIndex,
  uploadProgressSlot,
  onVideoAttached,
  onVideoCaptured,
  studioOnly = false,
}) => {
  const [recordModalStep, setRecordModalStep] = useState<number>(1);
  const [videoMode, setVideoMode] = useState<'record' | 'upload'>('upload');
  const [temporaryVideoFile, setTemporaryVideoFile] = useState<File | null>(null);
  const [showManageExistingVideo, setShowManageExistingVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modalWasOpenRef = useRef(false);
  const [showTips, setShowTips] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const recordStartedAtRef = useRef<number | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [fixedPreviewUrl, setFixedPreviewUrl] = useState<string | null>(null);
  const [isStageFullscreen, setIsStageFullscreen] = useState(false);
  const [isMobileStudioLayout, setIsMobileStudioLayout] = useState(false);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine
  );
  const currentStreamRef = useRef<MediaStream | null>(null);
  const attachedPlaybackUrlRef = useRef<string | null>(null);
  const livePreviewTrackIdRef = useRef<string | null>(null);
  const processRecordingRef = useRef<(blob: Blob) => Promise<void>>(async () => {});
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const documentTargetIndex = openPromptIndex ?? editingIndex;
  const hasExistingVideo = showManageExistingVideo;

  useEffect(() => {
    const justOpened = recordOrUploadModal && !modalWasOpenRef.current;
    modalWasOpenRef.current = recordOrUploadModal;

    if (!recordOrUploadModal) {
      setShowManageExistingVideo(false);
      return;
    }

    if (justOpened) {
      const idx = openPromptIndex ?? editingIndex;
      setShowManageExistingVideo(!hideSubmit && idx !== null && Boolean(videoFiles?.[idx]));
    }
  }, [recordOrUploadModal, openPromptIndex, editingIndex, hideSubmit, videoFiles]);

  useEffect(() => {
    if (recordOrUploadModal && studioOnly) {
      setRecordModalStep(2);
      setVideoMode('record');
    }
  }, [recordOrUploadModal, studioOnly]);

  const finishVideoAttach = useCallback(
    (fileOverride?: File | null) => {
      if (hideSubmit) {
        const captured = fileOverride ?? temporaryVideoFile;
        toast.success('Video added successfully!');
        if (onVideoCaptured && captured) {
          onVideoCaptured(captured);
        } else if (onVideoAttached) {
          onVideoAttached();
        } else {
          onModalClose?.();
        }
        return true;
      }
      return false;
    },
    [hideSubmit, onModalClose, onVideoAttached, onVideoCaptured, temporaryVideoFile]
  );

  const handleReplaceViaUpload = () => {
    if (documentTargetIndex == null) {
      return;
    }
    onClearVideoAtIndex?.(documentTargetIndex);
    setShowManageExistingVideo(false);
    setVideoFiles((prevFiles: Record<number, File>) => {
      const updatedFiles = { ...prevFiles };
      delete updatedFiles[documentTargetIndex];
      return updatedFiles;
    });
    setTemporaryVideoFile(null);
    setVideoMode('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleReplaceViaRecord = () => {
    if (documentTargetIndex == null) {
      return;
    }
    clearRecordingState(documentTargetIndex);
    setShowManageExistingVideo(false);
    setVideoMode('record');
    setRecordModalStep(2);
  };

  const recordingMimeType = useMemo(() => getRecordingMimeType(), []);
  const mediaRecorderOptions = useMemo(
    () => (recordingMimeType ? { mimeType: recordingMimeType } : undefined),
    [recordingMimeType]
  );
  const shouldAcquireMediaOnMount = Boolean(
    preferRecordingStudio && recordOrUploadModal && (studioOnly || recordModalStep === 2)
  );

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
    previewStream,
    previewAudioStream,
  } = useReactMediaRecorder({
    video: PROMPT_RECORDING_MEDIA.video ?? true,
    audio: PROMPT_RECORDING_MEDIA.audio ?? true,
    askPermissionOnMount: shouldAcquireMediaOnMount,
    stopStreamsOnStop: false,
    mediaRecorderOptions,
    onStop: (_url, blob) => {
      void processRecordingRef.current(blob);
    },
  });
  const isRecording = status === 'recording';
  const previewVideoTrackId = previewStream?.getVideoTracks()[0]?.id ?? null;
  const studioStreamReady = Boolean(
    previewStream?.getVideoTracks().some((track) => track.readyState === 'live') &&
      previewAudioStream?.getAudioTracks().some((track) => track.readyState === 'live')
  );
  const awaitingPlayback =
    isProcessingRecording || status === 'stopping' || Boolean(mediaBlobUrl && !fixedPreviewUrl);
  const hasRecordedPreview = Boolean(fixedPreviewUrl);
  const isPlaybackPreview = Boolean(fixedPreviewUrl && !isRecording);

  processRecordingRef.current = async (blob: Blob) => {
    const targetIndex = openPromptIndex ?? editingIndex;
    setIsProcessingRecording(true);
    try {
      if (targetIndex == null || !blob.size) {
        if (!blob.size) {
          toast.error('Recording failed. Please try again.');
        }
        return;
      }

      const durationMs = recordStartedAtRef.current
        ? Math.max(Date.now() - recordStartedAtRef.current, 500)
        : Math.max(recordingSeconds, 1) * 1000;
      recordStartedAtRef.current = null;

      const fixedBlob = await patchRecordedWebmDuration(blob, durationMs);
      const ext = fixedBlob.type.split('/')[1] || 'webm';
      const previewUrl = URL.createObjectURL(fixedBlob);
      setFixedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return previewUrl;
      });
      setTemporaryVideoFile(
        new File([fixedBlob], `prompt-${targetIndex}.${ext}`, {
          type: fixedBlob.type || 'video/webm',
        })
      );
    } catch (err) {
      console.error('Error preparing recording preview:', err);
      toast.error('Could not prepare your recording preview. Please try again.');
      setFixedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setTemporaryVideoFile(null);
    } finally {
      setIsProcessingRecording(false);
      clearBlobUrl();
    }
  };
  const [hasLivePreview, setHasLivePreview] = useState(false);

  const cameraEnabled = isPlaybackPreview || hasLivePreview;
  const microphoneEnabled =
    isPlaybackPreview ||
    Boolean(
      previewAudioStream
        ?.getAudioTracks()
        .some((track) => track.readyState === 'live' && track.enabled !== false)
    );

  const cameraStatusLabel = isPlaybackPreview ? '(Preview)' : cameraEnabled ? '(On)' : '(Off)';
  const microphoneStatusLabel = isPlaybackPreview
    ? '(Preview)'
    : microphoneEnabled
      ? '(On)'
      : '(Off)';

  useEffect(() => {
    const syncOnline = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', syncOnline);
    window.addEventListener('offline', syncOnline);
    return () => {
      window.removeEventListener('online', syncOnline);
      window.removeEventListener('offline', syncOnline);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => setIsMobileStudioLayout(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      const stage = stageRef.current;
      setIsStageFullscreen(Boolean(stage && document.fullscreenElement === stage));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (recordModalStep === 2) return;

    const stage = stageRef.current;
    if (!stage || document.fullscreenElement !== stage) return;

    try {
      void document.exitFullscreen();
    } catch {
      // Modal step changes can run while the document is inactive.
    }
  }, [recordModalStep]);

  const toggleStageFullscreen = async () => {
    const stage = stageRef.current;
    if (!stage) return;

    try {
      if (document.fullscreenElement === stage) {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } else {
        await stage.requestFullscreen();
      }
    } catch (err) {
      console.error('Unable to toggle recording fullscreen:', err);
    }
  };

  const triggerUploadVideo = () => {
    setVideoMode('upload');
    setErrors((prevErrors: any) => ({ ...prevErrors, videoFile: '' }));
  };

  const uploadPreviewUrl = useMemo(() => {
    if (videoMode !== 'upload' || !temporaryVideoFile) {
      return null;
    }
    return URL.createObjectURL(temporaryVideoFile);
  }, [videoMode, temporaryVideoFile]);

  useEffect(() => {
    return () => {
      if (uploadPreviewUrl) {
        URL.revokeObjectURL(uploadPreviewUrl);
      }
    };
  }, [uploadPreviewUrl]);

  const handleStartRecordingInitial = useCallback(() => {
    clearBlobUrl();
    setErrors((prevErrors: any) => ({ ...prevErrors, videoFile: '' }));
    setShowTips(false);

    if (preferRecordingStudio) {
      const hasLiveTracks = studioStreamReady;
      if (!hasLiveTracks) {
        toast.error('Camera is still loading. Please wait a moment and try again.');
        return;
      }
    }

    setCountdown(5);
  }, [clearBlobUrl, setErrors, preferRecordingStudio, studioStreamReady]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    if (status === 'recording') {
      if (!recordStartedAtRef.current) {
        recordStartedAtRef.current = Date.now();
      }
      timer = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [status]);

  const handleStopRecording = useCallback(() => {
    setIsProcessingRecording(true);
    stopRecording();
  }, [stopRecording]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (status === 'recording') {
        handleStopRecording();
      }
    }, 120 * 1000);
    return () => clearTimeout(timeout);
  }, [status, handleStopRecording]);

  const validateVideoDuration = (
    file: File,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    const video = document.createElement('video');
    console.log('video:', file.type);
    video.preload = 'metadata';

    let retryCount = 0;
    const maxRetries = 4;

    const handleMetadataLoad = () => {
      if (isNaN(video.duration) || video.duration === Infinity) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(() => {
            video.src = '';
            video.src = URL.createObjectURL(file);
          }, 500);
        } else {
          window.URL.revokeObjectURL(video.src);
          onError('Unable to load the video. Please try a different file.');
        }
        return;
      }

      window.URL.revokeObjectURL(video.src);

      if (video.duration <= 120) {
        onSuccess();
      } else {
        onError('Video is longer than 2 minutes');
      }
    };

    video.onloadedmetadata = handleMetadataLoad;

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      console.log(video.error);
      onError('Error loading video file');
    };

    video.src = URL.createObjectURL(file);
  };

  const uploadVideo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          videoFile: 'File size exceeds 20MB',
        }));
        input.value = '';
        return;
      }

      if (
        file.type !== 'video/webm' &&
        file.type !== 'video/mp4' &&
        file.type !== 'video/quicktime'
      ) {
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          videoFile: 'File type must be webm, mp4, or MOV',
        }));
        input.value = '';
        return;
      }

      validateVideoDuration(
        file,
        () => {
          const newFile = new File([file], file.name, { type: file.type });

          const targetIndex = openPromptIndex ?? editingIndex;

          if (targetIndex != null) {
            setVideoFiles((prev: any) => ({
              ...prev,
              [targetIndex]: newFile,
            }));
          }

          setTemporaryVideoFile(newFile);
          setErrors((prevErrors: any) => ({
            ...prevErrors,
            videoFile: '',
          }));
          input.value = '';

          if (finishVideoAttach(newFile)) {
            return;
          }

          toast.success('Video added successfully!');
        },
        (error: string) => {
          setErrors((prevErrors: any) => ({
            ...prevErrors,
            videoFile: error,
          }));
          toast.error(error || 'Invalid video.');
          input.value = '';
        }
      );
    }
  };

  /*   useEffect(() => {
    setTimeout(() => {
      if (status === 'recording') {
        // Ensure recording is still ongoing
        handleStopRecording();
      }
    }, 120 * 1000);
  }, [status, handleStopRecording]); */

  useEffect(() => {
    if (status === 'media_aborted') {
      toast.error(`${validationMessages.RECORDING_ABORTED}`);
    } else if (status === 'permission_denied') {
      toast.error(getRecordingPermissionErrorMessage(new DOMException('', 'NotAllowedError')));
    } else if (status === 'no_specified_media_found') {
      toast.error(`${validationMessages.NO_MEDIA_FOUND}`);
    } else if (status === 'media_in_use') {
      toast.error(`${validationMessages.MEDIA_IN_USE}`);
    } else if (status === 'invalid_media_constraints') {
      toast.error(`${validationMessages.INVALID_MEDIA_CONSTRAINS}`);
    }
  }, [status]);

  const clearRecordingState = useCallback(
    (targetIndex?: number | null, options?: { forRerecord?: boolean }) => {
      const forRerecord = options?.forRerecord ?? false;
      const idx = targetIndex ?? openPromptIndex ?? editingIndex;
      if (idx != null) {
        onClearVideoAtIndex?.(idx);
        setVideoFiles((prev: Record<number, File>) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      clearBlobUrl();
      setFixedPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setTemporaryVideoFile(null);
      setRecordingSeconds(0);
      recordStartedAtRef.current = null;
      setCountdown(null);
      setShowTips(false);
      setIsProcessingRecording(false);
      attachedPlaybackUrlRef.current = null;
      livePreviewTrackIdRef.current = null;
      const el = videoRef.current;
      if (el) {
        if (!forRerecord && el.srcObject instanceof MediaStream) {
          (el.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        }
        el.pause();
        el.srcObject = null;
        el.removeAttribute('src');
        el.load();
        if (forRerecord) {
          currentStreamRef.current = null;
        }
      }
      clearBlobUrl();
      if (!forRerecord) {
        try {
          stopRecording();
        } catch {
          /* recorder may already be idle */
        }
      }
    },
    [openPromptIndex, editingIndex, setVideoFiles, clearBlobUrl, stopRecording, onClearVideoAtIndex]
  );

  const handleContinueWithVideo = useCallback(() => {
    const targetIndex = openPromptIndex ?? editingIndex;
    if (temporaryVideoFile && targetIndex != null) {
      setVideoFiles((prev: Record<number, File>) => ({
        ...prev,
        [targetIndex]: temporaryVideoFile,
      }));
    }
    if (finishVideoAttach(temporaryVideoFile)) {
      return;
    }
    setRecordModalStep(3);
  }, [temporaryVideoFile, openPromptIndex, editingIndex, setVideoFiles, finishVideoAttach]);

  const handleReRecord = useCallback(() => {
    const targetIndex = openPromptIndex ?? editingIndex;
    clearRecordingState(targetIndex, { forRerecord: true });
    handleStartRecordingInitial();
  }, [openPromptIndex, editingIndex, clearRecordingState, handleStartRecordingInitial]);

  useEffect(
    () => () => {
      if (fixedPreviewUrl) URL.revokeObjectURL(fixedPreviewUrl);
    },
    [fixedPreviewUrl]
  );

  const beginRecordingAfterCountdown = useCallback(() => {
    if (preferRecordingStudio && !studioStreamReady) {
      toast.error('Camera disconnected before recording could start. Please try again.');
      setCountdown(null);
      return;
    }

    recordStartedAtRef.current = Date.now();
    startRecording();
    setCountdown(null);
  }, [preferRecordingStudio, studioStreamReady, startRecording]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined; // Explicitly type timer

    if (countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000); // Decrease countdown every second
    } else if (countdown === 0) {
      beginRecordingAfterCountdown();
    }

    return () => clearTimeout(timer); // Clear timer on component unmount or countdown change
  }, [countdown, beginRecordingAfterCountdown]);

  const handleCloseTips = () => {
    setShowTips(false); // Hide tips
    setCountdown(5); // Start countdown from 5 seconds
  };

  const handleSubmit = useCallback(async () => {
    if (!temporaryVideoFile) return;
    setSubmitting(true);
    try {
      // --- EDIT FLOW ---
      if (editingIndex && editingIndex > 1000) {
        const fd = new FormData();
        fd.append(
          'prompts',
          JSON.stringify([{ id: editingIndex, fileId: `file-${editingIndex}` }])
        );
        fd.append(`file-${editingIndex}`, temporaryVideoFile, temporaryVideoFile.name);

        await axiosInstance.put(`prompt/${editingIndex}`, fd);
        toast.success('Video updated');

        setVideoFiles((prev: any) => ({
          ...prev,
          [editingIndex]: temporaryVideoFile,
        }));
      } else {
        // --- CREATE FLOW ---
        setVideoFiles((prev: any) => ({
          ...prev,
          [openPromptIndex!]: temporaryVideoFile,
        }));
        toast.success('Video attached (will save when you submit)');
      }

      //setTemporaryVideoFile(null);
      onModalClose?.();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save video');
    } finally {
      setSubmitting(false);
    }
  }, [temporaryVideoFile, editingIndex, openPromptIndex, setVideoFiles, onModalClose]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useEffect(() => {
    if (recordOrUploadModal) return; // only run when closing

    setEditingIndex?.(null);
    setRecordModalStep(1);
    setVideoMode('upload');
    setTemporaryVideoFile(null);
    setErrors((prev: any) => ({ ...prev, videoFile: '' }));
    setShowTips(false);
    setCountdown(null);
    setRecordingSeconds(0);
    setIsProcessingRecording(false);
    attachedPlaybackUrlRef.current = null;
    livePreviewTrackIdRef.current = null;

    // stop recording if needed
    try {
      stopRecording();
    } catch {}

    // properly clear the video element and stop tracks
    const el = videoRef.current;
    if (el) {
      try {
        if (el.srcObject instanceof MediaStream) {
          (el.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        }
        el.pause();
        el.srcObject = null;
        el.removeAttribute('src');
        el.load();
      } catch {}
    }

    clearBlobUrl();
    setFixedPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [recordOrUploadModal, setEditingIndex, setErrors, stopRecording, clearBlobUrl]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (awaitingPlayback) {
      if (el.srcObject instanceof MediaStream) {
        el.srcObject = null;
        currentStreamRef.current = null;
      }
      if (el.src) {
        el.pause();
        el.removeAttribute('src');
      }
      attachedPlaybackUrlRef.current = null;
      livePreviewTrackIdRef.current = null;
      return;
    }

    if (fixedPreviewUrl) {
      if (attachedPlaybackUrlRef.current === fixedPreviewUrl) {
        return;
      }
      attachedPlaybackUrlRef.current = fixedPreviewUrl;
      livePreviewTrackIdRef.current = null;
      if (el.srcObject instanceof MediaStream) {
        el.srcObject = null;
        currentStreamRef.current = null;
      }
      el.src = fixedPreviewUrl;
      el.muted = false;
      el.controls = true;
      el.preload = 'auto';
      // @ts-ignore
      el.playsInline = true;
      void el.play().catch(() => {});
      return;
    }

    attachedPlaybackUrlRef.current = null;

    if (!previewVideoTrackId || !previewStream) {
      return;
    }

    if (
      livePreviewTrackIdRef.current === previewVideoTrackId &&
      el.srcObject instanceof MediaStream
    ) {
      return;
    }

    if (el.src) {
      el.pause();
      el.removeAttribute('src');
    }
    livePreviewTrackIdRef.current = previewVideoTrackId;
    currentStreamRef.current = previewStream;
    el.srcObject = previewStream;
    el.muted = true;
    el.controls = false;
    // @ts-ignore
    el.playsInline = true;
    el.autoplay = true;
    void el.play().catch(() => {});
    // previewStream identity changes every render; previewVideoTrackId gates re-attachment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingPlayback, fixedPreviewUrl, previewVideoTrackId]);

  useEffect(() => {
    if (recordModalStep !== 2) {
      setHasLivePreview(false);
      return;
    }

    if (fixedPreviewUrl) {
      setHasLivePreview(true);
      return;
    }

    if (awaitingPlayback) {
      setHasLivePreview(false);
      return;
    }

    setHasLivePreview(
      Boolean(
        previewStream
          ?.getVideoTracks()
          .some((track) => track.readyState === 'live' && track.enabled !== false)
      )
    );
    // previewStream is a new object each render; previewVideoTrackId is the stable signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordModalStep, fixedPreviewUrl, awaitingPlayback, previewVideoTrackId]);

  useEffect(() => {
    const el = videoRef.current; // snapshot once here

    return () => {
      if (el?.srcObject instanceof MediaStream) {
        (el.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        el.srcObject = null;
      }
    };
  }, []);

  useEffect(() => {
    const idx = openPromptIndex ?? editingIndex;
    if (idx != null && videoFiles?.[idx]) {
      setTemporaryVideoFile(videoFiles[idx]);
    } else {
      setTemporaryVideoFile(null);
    }
  }, [openPromptIndex, editingIndex, videoFiles]);

  const handleDeleteVideo = (index: number) => {
    clearRecordingState(index);
    setShowManageExistingVideo(false);
    setRecordModalStep(1);
    setVideoMode('upload');
  };

  const handleNextFromVideo = async () => {
    if (!temporaryVideoFile) {
      return;
    }

    const targetIndex = openPromptIndex ?? editingIndex;
    if (targetIndex != null) {
      setVideoFiles((prev: any) => ({
        ...prev,
        [targetIndex]: temporaryVideoFile,
      }));
    }

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      videoFile: '',
    }));

    setRecordModalStep(3);
  };

  const handleFinalize = async () => {
    await handleSubmit();
  };

  const renderInlineRecordingPanel = () => (
    <div className="mt-4 rounded-2xl border border-emerald-100 bg-white p-4 lg:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Record a response</div>
          <p className="mt-1 text-xs text-slate-600">
            We&apos;ll start recording after a 5-second countdown.
          </p>
        </div>
        {isRecording ? (
          <button className="pv2-btn-danger" type="button" onClick={handleStopRecording}>
            Stop
          </button>
        ) : (
          <button
            className="pv2-btn-primary"
            type="button"
            onClick={handleStartRecordingInitial}
            disabled={countdown !== null || (preferRecordingStudio && !studioStreamReady)}
          >
            Start recording
          </button>
        )}
      </div>

      <div className="relative mt-3">
        <video
          ref={videoRef}
          className="h-[min(420px,52vh)] w-full rounded-xl border border-slate-200 bg-slate-50 object-cover"
          playsInline
          autoPlay
        />
        {countdown !== null ? (
          <div className="absolute inset-0 grid place-items-center rounded-xl bg-black/50">
            <div className="text-5xl font-extrabold text-white">{countdown}</div>
          </div>
        ) : null}
        {isRecording && countdown === null ? (
          <div className="absolute right-3 top-3 rounded-full bg-slate-900/75 px-3 py-1 text-xs font-bold text-white">
            {formatTime(recordingSeconds)}
          </div>
        ) : null}
        {awaitingPlayback ? (
          <div className="absolute inset-0 grid place-items-center rounded-xl bg-slate-900/80">
            <div className="recording-studio-processing">
              <Spinner animation="border" size="sm" role="status" />
              <span>Preparing your preview…</span>
            </div>
          </div>
        ) : null}
      </div>

      {hasRecordedPreview ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {hideSubmit && temporaryVideoFile ? (
            <button
              className="pv2-btn-primary"
              type="button"
              onClick={() => {
                const targetIndex = openPromptIndex ?? editingIndex;
                if (targetIndex != null && temporaryVideoFile) {
                  setVideoFiles((prev: Record<number, File>) => ({
                    ...prev,
                    [targetIndex]: temporaryVideoFile,
                  }));
                  if (!finishVideoAttach(temporaryVideoFile)) {
                    toast.success('Recording ready — click Save to upload.');
                  }
                }
              }}
            >
              Use this recording
            </button>
          ) : null}
          <button
            className="pv2-btn-secondary"
            type="button"
            onClick={() => {
              const targetIndex = openPromptIndex ?? editingIndex;
              clearRecordingState(targetIndex);
              if (preferRecordingStudio) {
                setRecordModalStep(2);
                setVideoMode('record');
              }
            }}
          >
            Re-record
          </button>
        </div>
      ) : null}
    </div>
  );

  const useLegacyRecordingOverlay = !preferRecordingStudio;

  return (
    <>
      {useLegacyRecordingOverlay && showTips && recordModalStep !== 1 && (
        <div className="rec-shell">
          <div className="rec-stage">
            <div className="tips-panel">
              <h3 className="mb-2">Recording Tips</h3>
              <ul className="mb-3" style={{ margin: 0 }}>
                <li>Make sure you're in a quiet environment.</li>
                <li>Check your lighting for a clear picture.</li>
                <li>Position the camera at eye level.</li>
                <li>Speak clearly and confidently.</li>
              </ul>
              <Button variant="success" onClick={handleCloseTips}>
                Start in 5s
              </Button>
            </div>
          </div>
        </div>
      )}

      {useLegacyRecordingOverlay && !showTips && countdown !== null && recordModalStep !== 1 && (
        <div className="rec-shell">
          <div className="rec-stage">
            <div className="tips-panel" style={{ background: 'rgba(0,0,0,.6)', color: '#fff' }}>
              <h3 style={{ textAlign: 'center', width: '100%' }}>
                Recording will start in {countdown}...
              </h3>
            </div>
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        title="Upload video response"
        style={{ display: 'none' }}
        onChange={uploadVideo}
        accept="video/*"
      />
      {recordModalStep === 1 && (
        <Modal.Body className="record-upload-v2-body p-0">
          <div
            id="tailwind-root"
            className={`record-upload-v2-root mx-auto w-full p-4 sm:p-5 ${
              hasExistingVideo
                ? 'record-upload-v2-root--manage max-w-3xl'
                : videoMode === 'record'
                  ? 'record-upload-v2-root--record max-w-5xl'
                  : 'max-w-3xl'
            }`}
          >
            {hasExistingVideo ? (
              <div className="video-response-manage">
                <div className="video-response-manage__header">
                  <div className="video-response-manage__header-icon" aria-hidden>
                    <CameraVideoFill size={22} />
                  </div>
                  <div className="video-response-manage__header-copy">
                    <h3 className="video-response-manage__title">Video response</h3>
                    {promptText ? (
                      <p className="video-response-manage__prompt">{promptText}</p>
                    ) : null}
                  </div>
                  {onModalClose ? (
                    <button
                      type="button"
                      className="video-response-manage__close"
                      onClick={onModalClose}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  ) : null}
                </div>

                <div className="video-response-manage__notice" role="status">
                  <CheckCircleFill className="video-response-manage__notice-icon" aria-hidden />
                  <p>
                    You already have a video response for this prompt. What would you like to do?
                  </p>
                </div>

                <div className="video-response-manage__actions">
                  <button
                    type="button"
                    className="video-response-action video-response-action--primary"
                    onClick={handleReplaceViaUpload}
                  >
                    <span
                      className="video-response-action__icon video-response-action__icon--upload"
                      aria-hidden
                    >
                      <CloudUploadFill size={18} />
                    </span>
                    <span className="video-response-action__copy">
                      <strong>Replace via Upload</strong>
                      <span>Upload a new video file</span>
                    </span>
                    <ChevronRight className="video-response-action__chevron" aria-hidden />
                  </button>

                  <button
                    type="button"
                    className="video-response-action video-response-action--primary"
                    onClick={handleReplaceViaRecord}
                  >
                    <span
                      className="video-response-action__icon video-response-action__icon--record"
                      aria-hidden
                    >
                      <RecordCircleFill size={18} />
                    </span>
                    <span className="video-response-action__copy">
                      <strong>Replace via Record</strong>
                      <span>Record a new video</span>
                    </span>
                    <ChevronRight className="video-response-action__chevron" aria-hidden />
                  </button>

                  <button
                    type="button"
                    className="video-response-action video-response-action--danger"
                    onClick={() => handleDeleteVideo(documentTargetIndex!)}
                  >
                    <span
                      className="video-response-action__icon video-response-action__icon--danger"
                      aria-hidden
                    >
                      <DeleteIcon width={18} height={18} />
                    </span>
                    <span className="video-response-action__copy">
                      <strong>Delete video</strong>
                      <span>Remove your current video response</span>
                    </span>
                    <ChevronRight
                      className="video-response-action__chevron video-response-action__chevron--danger"
                      aria-hidden
                    />
                  </button>

                  <button
                    type="button"
                    className="video-response-action video-response-action--neutral"
                    onClick={onModalClose}
                  >
                    <span
                      className="video-response-action__icon video-response-action__icon--neutral"
                      aria-hidden
                    >
                      <FileEarmarkFill size={18} />
                    </span>
                    <span className="video-response-action__copy">
                      <strong>Keep current video</strong>
                      <span>Keep the existing video as is</span>
                    </span>
                    <ChevronRight className="video-response-action__chevron" aria-hidden />
                  </button>
                </div>

                <div className="video-response-manage__footer">
                  <button
                    type="button"
                    className="video-response-manage__cancel"
                    onClick={onModalClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {promptText ? (
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Add video response</h3>
                      <p className="mt-1 text-sm text-slate-600">{promptText}</p>
                    </div>
                    {onModalClose ? (
                      <button
                        className="text-sm text-slate-500 hover:text-slate-700"
                        onClick={onModalClose}
                        type="button"
                      >
                        Close
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="mt-2">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <button
                      className={`rounded-2xl border p-5 text-center ${videoMode === 'record' ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-100 bg-white'}`}
                      onClick={async () => {
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        setVideoMode('record');
                        setErrors((prevErrors: any) => ({
                          ...prevErrors,
                          videoFile: '',
                        }));
                        if (preferRecordingStudio) {
                          setRecordModalStep(2);
                        }
                      }}
                      type="button"
                    >
                      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-emerald-600 text-lg text-white">
                        ▶
                      </div>
                      <div className="font-semibold text-slate-900">Record Response</div>
                      {preferRecordingStudio ? (
                        <p className="mt-2 text-xs text-slate-500">
                          Opens the recording studio with tips
                        </p>
                      ) : null}
                    </button>
                    <button
                      className={`rounded-2xl border p-5 text-center ${videoMode === 'upload' ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-100 bg-white'}`}
                      onClick={triggerUploadVideo}
                      type="button"
                    >
                      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-sky-600 text-lg text-white">
                        ↑
                      </div>
                      <div className="font-semibold text-slate-900">Upload Video</div>
                    </button>
                  </div>

                  {videoMode === 'upload' ? (
                    <div className="mt-4 rounded-2xl border border-emerald-100 bg-white p-4">
                      <div className="text-sm font-semibold text-slate-900">
                        Choose a video from your device
                      </div>
                      <p className="mt-1 text-xs text-slate-600">
                        WEBM, MP4, or MOV up to 20MB and 2 minutes.
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                          className="pv2-btn-secondary"
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Select video
                        </button>
                        {temporaryVideoFile ? (
                          <span className="text-xs text-slate-600">{temporaryVideoFile.name}</span>
                        ) : (
                          <span className="text-xs text-slate-500">No file selected</span>
                        )}
                        {temporaryVideoFile ? (
                          <button
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                            type="button"
                            onClick={() => setTemporaryVideoFile(null)}
                          >
                            <DeleteIcon width={14} height={14} />
                            Remove
                          </button>
                        ) : null}
                      </div>

                      {uploadProgressSlot ? <div className="mt-4">{uploadProgressSlot}</div> : null}

                      {uploadPreviewUrl ? (
                        <div className="mt-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                            Preview
                          </div>
                          <video
                            className="mt-2 max-h-[360px] w-full rounded-xl border border-slate-200 bg-slate-50"
                            controls
                            src={uploadPreviewUrl}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {videoMode === 'record' && recordModalStep === 1 && !preferRecordingStudio
                    ? renderInlineRecordingPanel()
                    : null}

                  {errors.videoFile ? (
                    <p className="mt-3 text-sm text-red-600">{errors.videoFile}</p>
                  ) : null}

                  {!hideSubmit ? (
                    <div className="mt-6 flex items-center justify-between gap-3">
                      <button className="pv2-btn-secondary" type="button" onClick={onModalClose}>
                        Cancel
                      </button>
                      <button
                        className="pv2-btn-primary"
                        type="button"
                        onClick={handleNextFromVideo}
                        disabled={!temporaryVideoFile || submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Saving...
                          </>
                        ) : (
                          'Next'
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6 flex items-center justify-start gap-3">
                      <button className="pv2-btn-secondary" type="button" onClick={onModalClose}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      )}
      {recordModalStep === 2 && (
        <Modal.Body className="recording-studio-body recording-studio-body--studio">
          <div className="recording-studio-layout">
            <div className="recording-studio-main">
              <div className="recording-studio-top recording-studio-top--studio">
                <div className="recording-studio-heading">
                  <span className="recording-studio-heading__icon" aria-hidden>
                    <CameraVideo size={18} />
                  </span>
                  <div>
                    <h3 className="recording-studio-title">Recording studio</h3>
                    <p className="recording-studio-subtitle">
                      Record your answer to the prompt below. You can re-record anytime.
                    </p>
                  </div>
                </div>
                {onModalClose ? (
                  <button
                    type="button"
                    className="recording-studio-close-link"
                    onClick={onModalClose}
                  >
                    <X size={16} aria-hidden />
                    Close
                  </button>
                ) : null}
              </div>

              {promptText ? (
                <div className="recording-studio-prompt-bar">
                  <p className="recording-studio-prompt-bar__text">{promptText}</p>
                  <span className="recording-studio-prompt-bar__count">
                    {promptText.length} / 2500
                  </span>
                </div>
              ) : null}

              <div
                ref={stageRef}
                className={`recording-studio-stage-wrap recording-studio-stage-wrap--studio${
                  isStageFullscreen ? ' is-fullscreen' : ''
                }${hasRecordedPreview ? ' has-playback' : ''}${
                  awaitingPlayback ? ' is-processing' : ''
                }`}
              >
                <button
                  type="button"
                  className="recording-studio-expand-btn"
                  onClick={() => void toggleStageFullscreen()}
                  aria-label={isStageFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  title={isStageFullscreen ? 'Exit fullscreen' : 'Expand'}
                >
                  {isStageFullscreen ? (
                    <ArrowsAngleContract size={18} aria-hidden />
                  ) : (
                    <ArrowsFullscreen size={18} aria-hidden />
                  )}
                </button>

                <video
                  ref={videoRef}
                  className="recording-studio-video recording-studio-video--studio"
                  playsInline
                  autoPlay
                />

                {!hasRecordedPreview &&
                !awaitingPlayback &&
                !previewStream &&
                countdown === null ? (
                  <div className="recording-studio-stage-placeholder">
                    <span className="recording-studio-stage-placeholder__icon" aria-hidden>
                      <CameraVideoFill size={42} />
                    </span>
                    <strong>Ready to record?</strong>
                    <p>
                      Click the button below to start your recording. You&apos;ll see yourself here.
                    </p>
                  </div>
                ) : null}

                {countdown !== null ? (
                  <div className="recording-studio-countdown" aria-live="polite">
                    <span className="recording-studio-countdown-num">{countdown}</span>
                    <span className="recording-studio-countdown-label">Starting soon…</span>
                  </div>
                ) : null}

                {isRecording && countdown === null ? (
                  <div className="recording-studio-timer">{formatTime(recordingSeconds)}</div>
                ) : null}

                {awaitingPlayback ? (
                  <div
                    className="recording-studio-processing recording-studio-processing--stage"
                    aria-live="polite"
                  >
                    <Spinner animation="border" role="status" />
                    <span>Preparing your preview…</span>
                  </div>
                ) : null}

                {(isStageFullscreen || isMobileStudioLayout) &&
                (isRecording || hasRecordedPreview || awaitingPlayback || countdown === null) ? (
                  <div
                    className={`recording-studio-stage-overlay${
                      hasRecordedPreview ? ' recording-studio-stage-overlay--review' : ''
                    }${isMobileStudioLayout && !isStageFullscreen ? ' recording-studio-stage-overlay--mobile' : ''}`}
                  >
                    {hasRecordedPreview ? (
                      <>
                        <button
                          type="button"
                          className="recording-studio-secondary-cta recording-studio-secondary-cta--overlay"
                          onClick={handleReRecord}
                        >
                          <ArrowRepeat size={18} aria-hidden />
                          Re-record
                        </button>
                        <button
                          type="button"
                          className="recording-studio-primary-cta recording-studio-primary-cta--overlay"
                          onClick={handleContinueWithVideo}
                        >
                          Continue with this video
                        </button>
                      </>
                    ) : isRecording ? (
                      <button
                        type="button"
                        className="recording-studio-stop-cta recording-studio-stop-cta--overlay"
                        onClick={handleStopRecording}
                      >
                        <StopFill size={20} aria-hidden />
                        Stop recording
                      </button>
                    ) : awaitingPlayback ? null : countdown === null ? (
                      <button
                        type="button"
                        className="recording-studio-primary-cta recording-studio-primary-cta--overlay"
                        onClick={handleStartRecordingInitial}
                        disabled={preferRecordingStudio && !studioStreamReady}
                      >
                        <CameraVideoFill size={22} aria-hidden />
                        Start recording
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="recording-studio-actions-stack">
                {uploadProgressSlot ? (
                  <div className="recording-studio-upload-progress">{uploadProgressSlot}</div>
                ) : null}

                <div
                  className={`recording-studio-toolbar${
                    hasRecordedPreview ? ' recording-studio-toolbar--review' : ''
                  }`}
                >
                  {hasRecordedPreview ? (
                    <>
                      <button
                        type="button"
                        className="recording-studio-secondary-cta"
                        onClick={handleReRecord}
                      >
                        <ArrowRepeat size={18} aria-hidden />
                        Re-record
                      </button>
                      <button
                        type="button"
                        className="recording-studio-primary-cta"
                        onClick={handleContinueWithVideo}
                      >
                        Continue with this video
                      </button>
                    </>
                  ) : awaitingPlayback ? (
                    <div className="recording-studio-processing recording-studio-processing--toolbar">
                      <Spinner animation="border" size="sm" role="status" />
                      <span>Preparing your preview…</span>
                    </div>
                  ) : isRecording ? (
                    <button
                      type="button"
                      className="recording-studio-stop-cta"
                      onClick={handleStopRecording}
                    >
                      <StopFill size={20} aria-hidden />
                      Stop recording
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="recording-studio-primary-cta"
                      onClick={handleStartRecordingInitial}
                      disabled={countdown !== null || (preferRecordingStudio && !studioStreamReady)}
                    >
                      <CameraVideoFill size={22} aria-hidden />
                      Start recording
                    </button>
                  )}
                </div>
              </div>
            </div>

            <aside className="recording-studio-sidebar">
              <div className="recording-studio-status-row recording-studio-status-row--sidebar">
                <span className={`recording-studio-status-chip${cameraEnabled ? ' is-on' : ''}`}>
                  <CameraVideo size={14} aria-hidden />
                  Camera {cameraStatusLabel}
                </span>
                <span
                  className={`recording-studio-status-chip${microphoneEnabled ? ' is-on' : ''}`}
                >
                  <Mic size={14} aria-hidden />
                  Microphone {microphoneStatusLabel}
                </span>
                <span className={`recording-studio-status-chip${isOnline ? ' is-on' : ''}`}>
                  <Wifi size={14} aria-hidden />
                  Connection {isOnline ? '(Good)' : '(Offline)'}
                </span>
              </div>

              <div className="recording-studio-tips-panel">
                <h4>Tips for a great recording</h4>
                <ul>
                  <li>Good lighting on your face</li>
                  <li>Minimize background noise</li>
                  <li>Position the camera at eye level</li>
                  <li>Choose a quiet space with a clean background</li>
                </ul>
                <details className="recording-studio-tips-more">
                  <summary>View more tips</summary>
                  <ul>
                    <li>Speak naturally and look at the camera</li>
                    <li>Answer the specific question asked</li>
                    <li>Keep your answer clear and concise (60–90 seconds)</li>
                  </ul>
                </details>
              </div>

              <p className="recording-studio-privacy">
                <ShieldLock size={14} aria-hidden />
                Your recording is private and secure. Only authorized reviewers will be able to view
                it.
              </p>

              <div className="recording-studio-footer-note">
                <ShieldLock size={14} aria-hidden />
                You can review and re-record your answer before submitting.
              </div>
            </aside>
          </div>
        </Modal.Body>
      )}

      {recordModalStep === 3 && (
        <Modal.Body>
          <div className="support-doc-shell">
            <div className="support-doc-header">
              <h5 className="mb-1">Add Supporting Document (Optional)</h5>
              <p className="mb-0">
                Share a portfolio, case study, or credential to strengthen your response.
              </p>
            </div>

            <UploadSupportingDocument
              documentFiles={documentFiles ?? {}}
              setDocumentFiles={setDocumentFiles ?? (() => {})}
              openPromptIndex={documentTargetIndex}
              variant="modal"
            />

            <div className="support-doc-actions">
              <Button variant="outline-secondary" onClick={() => setRecordModalStep(1)}>
                Back
              </Button>
              <Button variant="light" className="support-doc-skip" onClick={handleFinalize}>
                Skip for now
              </Button>
              <Button variant="success" onClick={handleFinalize} disabled={submitting}>
                {submitting ? 'Saving...' : 'Next'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      )}
    </>
  );
};

export default RecordOrUploadModal;
